import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
    LayoutDashboard, FileText, CheckCircle, GraduationCap, Clock,
    Users, XCircle, Video, Zap, ArrowLeft
} from 'lucide-react';
import api from '../api';
import Loader from '../components/Loader';
import './VettingPage.css';

function VettingPage() {
    const navigate = useNavigate();
    const [activeMenu, setActiveMenu] = useState('dashboard');
    const [loading, setLoading] = useState(true);

    const [stats, setStats] = useState({
        pending_reviews: 0, approved_this_month: 0, approved_change: 0,
        declined_this_month: 0, declined_change: 0, avg_review_time_days: 0
    });

    const [applications, setApplications] = useState([]);
    const [approvedProfiles, setApprovedProfiles] = useState([]);
    const [interviewQueue, setInterviewQueue] = useState([]);
    const [trainingQueue, setTrainingQueue] = useState([]);
    const [selectedApp, setSelectedApp] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');

    const [showReviewModal, setShowReviewModal] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [reviewNotes, setReviewNotes] = useState('');
    const [processing, setProcessing] = useState(false);

    // Toast notification state
    const [toasts, setToasts] = useState([]);

    // Confirmation modal state
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        message: '',
        onConfirm: null
    });

    // Training schedule modal state
    const [trainingModal, setTrainingModal] = useState({
        show: false,
        appId: null,
        formData: {
            training_date: new Date().toISOString().split('T')[0],
            start_time: '11:00 AM',
            meeting_link: 'https://meet.google.com/...',
            trainer_name: 'Corporate Training Team'
        }
    });

    // Prompt modal state for interview link
    const [promptModal, setPromptModal] = useState({
        show: false,
        title: '',
        message: '',
        defaultValue: '',
        onSubmit: null
    });

    const [schedulingModal, setSchedulingModal] = useState({
        show: false,
        professor: null,
        selectedSlotId: '',
        customTimeslot: '',
        meetingLink: 'https://meet.google.com/...'
    });

    const [availableAdminSlots, setAvailableAdminSlots] = useState([]);

    const [adminSlots, setAdminSlots] = useState([]);
    const [smartModal, setSmartModal] = useState({
        show: false,
        selectedDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        startTime: '10:00',
        endTime: '17:00'
    });

    const handleSmartScheduleSubmit = async () => {
        if (smartModal.selectedDays.length === 0) {
            showToast('Please select at least one day', 'error');
            return;
        }

        try {
            setProcessing(true);
            // Generate slots for the next 4 weeks based on selected days and times
            const slots = [];
            const today = new Date();

            for (let i = 0; i < 28; i++) {
                const date = new Date(today);
                date.setDate(today.getDate() + i);
                const dayName = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);

                if (smartModal.selectedDays.includes(dayName)) {
                    const dateStr = date.toISOString().split('T')[0];
                    slots.push({
                        start_time: `${dateStr}T${smartModal.startTime}:00`,
                        end_time: `${dateStr}T${smartModal.endTime}:00`
                    });
                }
            }

            // We'll send them one by one for now since there's no bulk endpoint yet
            // In a real app, a bulk endpoint is better.
            await Promise.all(slots.map(slot => api.post('/vetting/admin-availability', slot)));

            showToast('Smart Schedule applied! Generated slots for next 4 weeks.', 'success');
            setSmartModal({ ...smartModal, show: false });
            fetchAdminSlots();
        } catch (err) {
            showToast('Failed to apply smart schedule', 'error');
        } finally {
            setProcessing(false);
        }
    };

    // Toast notification helper
    const showToast = (message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 4000);
    };


    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, appsRes] = await Promise.all([
                api.get('/vetting/dashboard-stats'),
                api.get('/vetting/applications')
            ]);
            setStats(statsRes.data);
            setApplications(appsRes.data || []);
            try {
                const approvedRes = await api.get('/vetting/approved-profiles');
                setApprovedProfiles(approvedRes.data || []);
            } catch { setApprovedProfiles([]); }
        } catch (err) {
            console.error('Fetch error:', err);
            setStats({ pending_reviews: 0, approved_this_month: 0, approved_change: 0, declined_this_month: 0, declined_change: 0, avg_review_time_days: 0 });
            setApplications([]);
            setApprovedProfiles([]);
        } finally { setLoading(false); }
    }, []);

    const fetchApplications = useCallback(async () => {
        try {
            const params = statusFilter ? { status: statusFilter } : {};
            const res = await api.get('/vetting/applications', { params });
            setApplications(res.data || []);
        } catch {
            console.error('Failed to fetch applications');
            setApplications([]);
        }
    }, [statusFilter]);

    const fetchApprovedProfiles = useCallback(async () => {
        try {
            const res = await api.get('/vetting/approved-profiles');
            setApprovedProfiles(res.data || []);
        } catch {
            console.error('Failed to fetch approved profiles');
            setApprovedProfiles([]);
        }
    }, []);

    const fetchInterviewQueue = useCallback(async () => {
        try {
            const res = await api.get('/vetting/interview-queue');
            setInterviewQueue(res.data || []);
        } catch (err) {
            console.error('Error fetching interview queue:', err);
            setInterviewQueue([]);
        }
    }, []);

    const fetchTrainingQueue = useCallback(async () => {
        try {
            const res = await api.get('/vetting/training-queue');
            setTrainingQueue(res.data || []);
        } catch (err) {
            console.error('Error fetching training queue:', err);
            setTrainingQueue([]);
        }
    }, []);

    const fetchAdminSlots = useCallback(async () => {
        try {
            const res = await api.get('/vetting/admin-availability');
            setAdminSlots(res.data || []);
        } catch (err) {
            console.error('Error fetching admin slots:', err);
        }
    }, []);

    useEffect(() => {
        const role = localStorage.getItem('role');
        if (!['admin', 'system_admin', 'support'].includes(role)) { navigate('/login'); return; }
        fetchDashboardData();

        // Polling removed as per request to prevent excessive 5-second refreshes.
        // Users can use the "Refresh Data" button in the UI instead.
    }, [navigate, fetchDashboardData]);

    useEffect(() => {
        if (activeMenu === 'applications') fetchApplications();
        else if (activeMenu === 'approved') fetchApprovedProfiles();
        else if (activeMenu === 'interviews') fetchInterviewQueue();
        else if (activeMenu === 'training') fetchTrainingQueue();
        else if (activeMenu === 'availability') fetchAdminSlots();
    }, [activeMenu, statusFilter, fetchApplications, fetchApprovedProfiles, fetchInterviewQueue, fetchTrainingQueue, fetchAdminSlots]);

    const calendarEvents = useMemo(() => {
        return adminSlots.map(slot => ({
            id: slot.id.toString(),
            start: slot.start_time,
            end: slot.end_time,
            title: slot.is_booked ? 'BOOKED' : 'Available',
            backgroundColor: slot.is_booked ? '#ef4444' : '#10b981',
            borderColor: slot.is_booked ? '#dc2626' : '#059669',
            extendedProps: { ...slot }
        }));
    }, [adminSlots]);

    const handleCalendarSelect = async (selectInfo) => {
        const calendarApi = selectInfo.view.calendar;
        calendarApi.unselect();

        setConfirmModal({
            show: true,
            message: `Add availability slot from ${new Date(selectInfo.startStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} to ${new Date(selectInfo.endStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}?`,
            onConfirm: async () => {
                setConfirmModal({ show: false, message: '', onConfirm: null });
                try {
                    await api.post('/vetting/admin-availability', {
                        start_time: selectInfo.startStr,
                        end_time: selectInfo.endStr
                    });
                    showToast('Slot added!', 'success');
                    fetchAdminSlots();
                } catch (err) {
                    showToast('Failed to add slot: ' + (err.response?.data?.detail || err.message), 'error');
                }
            }
        });
    };

    const handleEventClick = (clickInfo) => {
        const slot = clickInfo.event.extendedProps;
        if (slot.is_booked) {
            showToast('Cannot delete a booked slot', 'error');
            return;
        }

        setConfirmModal({
            show: true,
            message: 'Delete this availability slot?',
            onConfirm: async () => {
                setConfirmModal({ show: false, message: '', onConfirm: null });
                try {
                    await api.delete(`/vetting/admin-availability/${slot.id}`);
                    showToast('Slot deleted', 'success');
                    fetchAdminSlots();
                } catch (err) {
                    showToast('Failed to delete slot', 'error');
                }
            }
        });
    };

    const handleScheduleTraining = async (appId) => {
        setTrainingModal({
            show: true,
            appId: appId,
            formData: {
                training_date: new Date().toISOString().split('T')[0],
                start_time: '11:00 AM',
                meeting_link: 'https://meet.google.com/...',
                trainer_name: 'Corporate Training Team'
            }
        });
    };

    const submitTrainingSchedule = async () => {
        const { appId, formData } = trainingModal;

        try {
            await api.post(`/vetting/training/${appId}/schedule`, {
                training_date: formData.training_date,
                start_time: formData.start_time,
                meeting_link: formData.meeting_link,
                trainer_name: formData.trainer_name,
                duration_value: 1,
                duration_unit: "Hour"
            });
            showToast('Training scheduled successfully!', 'success');
            setTrainingModal({ show: false, appId: null, formData: {} });
            fetchTrainingQueue();
        } catch (err) {
            showToast('Failed to schedule training: ' + (err.response?.data?.detail || err.message), 'error');
        }
    };

    const handleMarkTrainingComplete = async (appId) => {
        setConfirmModal({
            show: true,
            message: "Confirm completing TRAINING stage for this professor?",
            onConfirm: async () => {
                setConfirmModal({ show: false, message: '', onConfirm: null });
                try {
                    await api.post(`/vetting/training/${appId}/complete`);
                    showToast('Training marked as COMPLETED!', 'success');
                    fetchTrainingQueue();
                } catch (err) {
                    showToast('Failed to complete training: ' + (err.response?.data?.detail || err.message), 'error');
                }
            }
        });
    };

    const handleMarkInterviewComplete = async (appId) => {
        try {
            await api.post(`/vetting/interview/${appId}/complete`);
            setInterviewQueue(prev => prev.map(item =>
                item.id === appId ? { ...item, interview_status: 'COMPLETED' } : item
            ));
            showToast('Interview marked as completed successfully.', 'success');
        } catch (err) {
            console.error('Error marking interview complete:', err);
            showToast('Failed to update interview status.', 'error');
        }
    };

    const fetchAvailableAdminSlots = async () => {
        try {
            const res = await api.get('/vetting/available-interview-slots');
            setAvailableAdminSlots(res.data || []);
        } catch (err) {
            console.error('Error fetching available slots:', err);
        }
    };

    const handleSchedulingSubmit = async () => {
        const { professor, selectedSlotId, customTimeslot, meetingLink } = schedulingModal;

        if (!meetingLink) {
            showToast('Please provide a meeting link', 'error');
            return;
        }

        if (!selectedSlotId && !customTimeslot) {
            showToast('Please select a slot or enter a custom time', 'error');
            return;
        }

        try {
            setProcessing(true);
            // Unified scheduling call
            await api.put(`/vetting/application/${professor.id}/schedule-interview`, {
                timeslot: customTimeslot,
                meeting_link: meetingLink,
                avail_id: selectedSlotId ? parseInt(selectedSlotId) : null
            });

            showToast('Interview scheduled successfully!', 'success');
            setSchedulingModal({ show: false, professor: null, selectedSlotId: '', customTimeslot: '', meetingLink: '' });
            fetchInterviewQueue();
        } catch (err) {
            console.error('Error scheduling interview:', err);
            showToast('Failed to schedule interview: ' + (err.response?.data?.detail || err.message), 'error');
        } finally {
            setProcessing(false);
        }
    };

    const [modalLoading, setModalLoading] = useState(false);

    const openReviewModal = async (appOrProfile) => {
        setModalLoading(true);
        setShowReviewModal(true);
        setActiveTab('overview');
        setReviewNotes('');

        await loadApplicationData(appOrProfile.id, appOrProfile);
        setModalLoading(false);
    };

    const loadApplicationData = async (appId, fallbackData = null) => {
        try {
            const res = await api.get(`/vetting/application/${appId}`);
            const data = res.data;

            // Parse AI analysis if it exists
            let aiData = data.ai_analysis || {};
            if (typeof aiData === 'string') {
                try { aiData = JSON.parse(aiData); } catch { aiData = {}; }
            }

            // Check if analysis is still pending (no AI data yet)
            const analysisPending = !data.analyzed_at || Object.keys(aiData).length === 0;

            // Merge API data with AI-extracted data
            setSelectedApp({
                ...data,
                analysisPending, // Flag to show refresh button
                // Use AI-extracted data if available, otherwise fall back to profile data
                professional_summary: aiData.professional_summary || `${data.full_name} is a ${data.job_title} at ${data.company}. Analysis pending...`,
                years_of_experience: aiData.years_of_experience || data.years_of_experience || 0,
                publications: aiData.publications || 0,
                prior_teaching: aiData.prior_teaching || 'Not specified',
                skills: aiData.skills || (data.expertise_areas ? data.expertise_areas.split(', ') : []),
                credibility_breakdown: aiData.credibility_breakdown || getDefaultBreakdown(),
                work_experience: aiData.work_experience || [],
                education: aiData.education || [],
                certifications: aiData.certifications || [],
                documents: buildDocumentsList(data, aiData.documents_analysis),
                timeline: buildTimeline(data),
                conflicts: aiData.conflicts || [],
                strengths: aiData.strengths || [],
                concerns: aiData.concerns || [],
                recommendation: aiData.recommendation || 'PENDING_REVIEW'
            });
        } catch (err) {
            console.error('Failed to fetch application detail:', err);
            // Fallback to basic data from the list
            if (fallbackData) {
                setSelectedApp({
                    ...fallbackData,
                    ...getMockDetailData(fallbackData),
                    analysisPending: true
                });
            }
        }
    };

    const refreshApplicationData = async () => {
        if (!selectedApp) return;
        setModalLoading(true);
        await loadApplicationData(selectedApp.id);
        setModalLoading(false);
    };

    const getDefaultBreakdown = () => ({
        experience: { score: 0, max: 25 },
        teaching_rating: { score: 0, max: 25 },
        research: { score: 0, max: 20 },
        interview_vetting: { score: 0, max: 20 },
        verification: { score: 0, max: 10 }
    });

    const formatDateTime = (d) => {
        if (!d) return 'Not set';
        return new Date(d).toLocaleString('en-GB', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const buildDocumentsList = (data, aiDocAnalysis) => {
        const docs = [];
        const analysis = aiDocAnalysis || {};

        // Helper to get filename from path (handle both forward and backslashes)
        const getFileName = (path, defaultName) => {
            if (!path) return defaultName;
            // Handle both Windows (\) and Unix (/) path separators
            const parts = path.split(/[/\\]/);
            return parts[parts.length - 1] || defaultName;
        };

        // Helper to determine status: if file exists, always mark as verified
        const getStatus = (hasPath, aiStatus) => {
            if (!hasPath) return 'missing';
            return 'verified'; // Always show verified if file exists
        };

        if (data.cv_resume_path) {
            docs.push({
                name: getFileName(data.cv_resume_path, 'Resume.pdf'),
                type: 'cv_resume',
                path: data.cv_resume_path,
                status: getStatus(true, analysis.cv_resume?.status),
                notes: analysis.cv_resume?.notes || 'Document uploaded'
            });
        } else {
            docs.push({ name: 'Resume.pdf', type: 'cv_resume', path: null, status: 'missing', notes: 'Not uploaded' });
        }

        if (data.degree_certificates_path) {
            docs.push({
                name: getFileName(data.degree_certificates_path, 'Degree Certificate.pdf'),
                type: 'degree_certificates',
                path: data.degree_certificates_path,
                status: getStatus(true, analysis.degree_certificates?.status),
                notes: analysis.degree_certificates?.notes || 'Document uploaded'
            });
        } else {
            docs.push({ name: 'Degree Certificate.pdf', type: 'degree_certificates', path: null, status: 'missing', notes: 'Not uploaded' });
        }



        // KYC Documents
        if (data.pan_card_path) {
            docs.push({
                name: getFileName(data.pan_card_path, 'PAN Card.pdf'),
                type: 'pan_card',
                path: data.pan_card_path,
                status: getStatus(true, analysis.pan_card?.status),
                notes: analysis.pan_card?.notes || 'KYC Document'
            });
        }
        if (data.aadhaar_card_path) {
            docs.push({
                name: getFileName(data.aadhaar_card_path, 'Aadhaar Card.pdf'),
                type: 'aadhaar_card',
                path: data.aadhaar_card_path,
                status: getStatus(true, analysis.aadhaar_card?.status),
                notes: analysis.aadhaar_card?.notes || 'KYC Document'
            });
        }

        return docs;
    };

    const buildTimeline = (data) => {
        const timeline = [
            {
                stage: 'Application Submitted',
                date: data.submitted_at,
                status: 'completed'
            },
            {
                stage: 'Automated Processing',
                date: data.analyzed_at,
                status: data.analyzed_at ? 'completed' : 'current'
            },
            {
                stage: 'Compliance/KYC Review',
                date: null,
                status: data.status === 'UNDER_REVIEW' ? 'current' : (data.reviewed_at ? 'completed' : 'pending')
            },
            {
                stage: 'Interview Stage',
                date: data.interview_timeslot,
                status: data.interview_status === 'COMPLETED' ? 'completed' : (data.interview_status === 'SCHEDULED' ? 'current' : 'pending')
            },
            {
                stage: 'Training Stage',
                date: null,
                status: (data.training_status === 'COMPLETED' || data.training_session?.status === 'COMPLETED') ? 'completed' :
                    ((data.interview_status === 'COMPLETED') ? 'current' : 'pending')
            },
            {
                stage: 'Final Approval',
                date: (data.training_status === 'COMPLETED' || data.training_session?.status === 'COMPLETED') ? data.reviewed_at : null,
                status: (data.training_status === 'COMPLETED' || data.training_session?.status === 'COMPLETED') ? 'completed' : 'pending'
            }
        ];
        return timeline;
    };


    const handleViewDocument = async (doc) => {
        if (doc.status === 'missing' || !doc.path) {
            showToast('Document not uploaded', 'error');
            return;
        }

        try {
            // Fetch PDF with authentication
            const baseUrl = api.defaults.baseURL || '';
            const viewUrl = `${baseUrl}/view-document/${doc.type}/${selectedApp.pop_user_id}`;

            const response = await api.get(viewUrl, {
                responseType: 'blob' // Important: get the response as a blob
            });

            // Create a blob URL and open in new tab
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');

            // Clean up the blob URL after a delay
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
            console.error('Error viewing document:', error);
            showToast('Failed to load document. Please try again.', 'error');
        }
    };


    const handleDecision = async (decision) => {
        if (!selectedApp) return;
        setProcessing(true);
        try {
            await api.put(`/vetting/application/${selectedApp.id}/decide`, { decision, notes: reviewNotes });
            showToast(`Application ${decision.toLowerCase()} successfully`, 'success');
        } catch { showToast(`Failed to ${decision.toLowerCase()} application`, 'error'); }
        setShowReviewModal(false);
        setSelectedApp(null);
        setProcessing(false);
        fetchDashboardData();
    };


    const getMockDetailData = (app) => ({
        email: `${app?.full_name?.toLowerCase().replace(/[^a-z]/g, '')}@company.com`,
        linkedin_url: 'https://linkedin.com/in/profile',
        years_of_experience: 20,
        publications: 0,
        prior_teaching: 'Guest Lecturer',
        professional_summary: `${app?.full_name || 'This professional'} brings 15+ years of experience in ${app?.domain || 'their field'}. Currently serving as ${app?.job_title || 'a leader'} at ${app?.company || 'a major company'} with focus on innovation and excellence.`,
        credibility_breakdown: {
            experience: { score: 22, max: 25 },
            teaching_rating: { score: 0, max: 25 },
            research: { score: 18, max: 20 },
            interview_vetting: { score: 0, max: 20 },
            verification: { score: 8, max: 10 }
        },
        work_experience: [
            { title: app?.job_title || 'Executive', company: app?.company || 'Company', period: '2021 - Present' },
            { title: 'Sr. ' + (app?.domain || 'Manager'), company: 'Google', period: '2018 - 2021' },
            { title: app?.domain || 'Specialist', company: 'Amazon', period: '2015 - 2018' }
        ],
        education: [
            { degree: 'PhD ' + (app?.domain || 'Science'), institution: 'MIT', year: '2015' },
            { degree: 'MS Computer Science', institution: 'Stanford', year: '2010' }
        ],
        documents: [
            { name: 'Resume.pdf', status: 'verified', type: 'cv' },
            { name: 'PhD Certificate.pdf', status: 'verified', type: 'degree' }
        ],
        timeline: [
            { stage: 'Application Submitted', date: app?.submitted_at || '2025-12-10', status: 'completed' },
            { stage: 'Automated Processing', date: app?.submitted_at || '2025-12-10', status: 'completed' },
            { stage: 'Compliance/KYC Review', date: null, status: 'current' },
            { stage: 'Final Approval', date: null, status: 'pending' }
        ],
        conflicts: []
    });

    const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 3) : '??';
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : 'N/A';
    const getScoreColor = (s) => !s || s === 0 ? '#9ca3af' : (s >= 80 ? '#22c55e' : s >= 60 ? '#f59e0b' : '#ef4444');
    const getStatusClass = (s) => ({ 'PENDING': 'pending', 'UNDER_REVIEW': 'review', 'APPROVED': 'approved', 'DECLINED': 'declined' }[s?.toUpperCase()] || 'pending');

    if (loading) return <Loader />;

    const pendingApps = applications.filter(a => ['PENDING', 'UNDER_REVIEW'].includes(a.status?.toUpperCase())).slice(0, 5);

    // Pre-calculate modal values to avoid issues with nested component
    const modalScore = selectedApp ? (selectedApp.ai_score || selectedApp.credibility_score || 87) : 0;
    const modalBreakdown = selectedApp ? (selectedApp.credibility_breakdown || getMockDetailData(selectedApp).credibility_breakdown) : {};

    return (
        <div className="vetting-layout">
            <aside className="vetting-sidebar">
                <nav className="sidebar-nav">
                    <button className={`sidebar-item ${activeMenu === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveMenu('dashboard')}>
                        <span className="sidebar-icon">
                            <LayoutDashboard size={18} />
                        </span>
                        Dashboard
                    </button>
                    <button className={`sidebar-item ${activeMenu === 'applications' ? 'active' : ''}`} onClick={() => setActiveMenu('applications')}>
                        <span className="sidebar-icon">
                            <FileText size={18} />
                        </span>
                        Applications
                        {stats.pending_reviews > 0 && <span className="sidebar-badge">{stats.pending_reviews}</span>}
                    </button>
                    <button className={`sidebar-item ${activeMenu === 'approved' ? 'active' : ''}`} onClick={() => setActiveMenu('approved')}>
                        <span className="sidebar-icon">
                            <CheckCircle size={18} />
                        </span>
                        Approved Profiles
                    </button>
                    {/* <button className={`sidebar-item ${activeMenu === 'interviews' ? 'active' : ''}`} onClick={() => setActiveMenu('interviews')}>
                        <span className="sidebar-icon">
                            <Video size={18} />
                        </span>
                        Interview
                    </button>
                    <button className={`sidebar-item ${activeMenu === 'training' ? 'active' : ''}`} onClick={() => setActiveMenu('training')}>
                        <span className="sidebar-icon">
                            <GraduationCap size={18} />
                        </span>
                        Training
                    </button>
                    <button className={`sidebar-item ${activeMenu === 'availability' ? 'active' : ''}`} onClick={() => setActiveMenu('availability')}>
                        <span className="sidebar-icon">
                            <Clock size={18} />
                        </span>
                        My Availability
                    </button> */}
                </nav>
            </aside>

            <main className="vetting-main">
                <header className="vetting-topbar">
                    <div className="topbar-title"><h1>Vetting Dashboard</h1></div>
                    <div className="topbar-actions">
                        <button onClick={() => { fetchDashboardData(); fetchApplications(); fetchApprovedProfiles(); fetchInterviewQueue(); fetchTrainingQueue(); }} className="admin-refresh-btn">
                            🔄 Refresh Data
                        </button>
                        <Link to="/admin-dashboard" className="admin-dashboard-btn">
                            <ArrowLeft size={18} style={{ marginRight: '8px' }} /> Admin Dashboard
                        </Link>
                    </div>
                </header>

                {activeMenu === 'dashboard' && (
                    <div className="vetting-content">
                        <p>{stats.pending_reviews} applications awaiting review</p>
                        <div className="stats-grid">
                            <div className="stat-card"><div className="stat-info"><span className="stat-label">Pending</span><span className="stat-value">{stats.pending_reviews}</span></div><div className="stat-icon pending"><Clock size={24} /></div></div>
                            <div className="stat-card">
                                <div className="stat-info">
                                    <span className="stat-label">Approved</span>
                                    <span className="stat-value">{stats.approved_this_month}</span>
                                    <span className="stat-trend positive">
                                        {(() => {
                                            const total = stats.pending_reviews + stats.approved_this_month + stats.declined_this_month;
                                            const percentage = total > 0 ? Math.round((stats.approved_this_month / total) * 100) : 0;
                                            return `${percentage}% of total`;
                                        })()}
                                    </span>
                                </div>
                                <div className="stat-icon approved"><Users size={24} /></div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-info">
                                    <span className="stat-label">Declined</span>
                                    <span className="stat-value">{stats.declined_this_month}</span>
                                    <span className="stat-trend negative">
                                        {(() => {
                                            const total = stats.pending_reviews + stats.approved_this_month + stats.declined_this_month;
                                            const percentage = total > 0 ? Math.round((stats.declined_this_month / total) * 100) : 0;
                                            return `${percentage}% of total`;
                                        })()}
                                    </span>
                                </div>
                                <div className="stat-icon declined"><XCircle size={24} /></div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-info">
                                    <span className="stat-label">Training Phase</span>
                                    <span className="stat-value">{stats.training_total || 0}</span>
                                    <span className="stat-trend highlight">
                                        {stats.training_today || 0} scheduled today
                                    </span>
                                </div>
                                <div className="stat-icon time"><GraduationCap size={24} /></div>
                            </div>
                        </div>
                        <div className="dashboard-sections">
                            <div className="applications-section"><div className="section-header"><h2>Pending Applications</h2><button className="view-all-btn" onClick={() => setActiveMenu('applications')}>View All</button></div>
                                <div className="applications-table">
                                    <div className="table-header">
                                        <span className="col-applicant">Applicant</span>
                                        <span className="col-role">Current Role</span>
                                        <span className="col-applied">Applied</span>
                                        <span className="col-score">Score</span>
                                        <span className="col-status">Status</span>
                                        <span className="col-actions">Actions</span>
                                    </div>
                                    {pendingApps.length === 0 ? (
                                        <div className="empty-state"><p>No pending applications</p></div>
                                    ) : (
                                        pendingApps.map(app => (
                                            <div className="table-row" key={app.id}>
                                                <div className="col-applicant">
                                                    <span className="applicant-avatar">{getInitials(app.full_name)}</span>
                                                    <span className="applicant-name">{app.full_name}</span>
                                                </div>
                                                <div className="col-role">
                                                    <span className="role-title">{app.job_title}</span>
                                                    <span className="role-company">{app.company}</span>
                                                </div>
                                                <div className="col-applied">{formatDate(app.submitted_at)}</div>
                                                <div className="col-score">
                                                    <span className="score-value" style={{ color: getScoreColor(app.ai_score) }}>
                                                        {app.ai_score || 'N/A'}
                                                    </span>
                                                </div>
                                                <div className="col-status">
                                                    <span className={`status-badge ${getStatusClass(app.status)}`}>
                                                        {app.status}
                                                    </span>
                                                </div>
                                                <div className="col-actions">
                                                    <button className="review-btn" onClick={() => openReviewModal(app)}>
                                                        Review
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div className="applications-section"><div className="section-header"><h2>Recently Approved</h2><button className="view-all-btn" onClick={() => setActiveMenu('approved')}>View All</button></div>
                                <div className="approved-profiles-grid">{approvedProfiles.slice(0, 4).map(profile => (<div className="approved-profile-card" key={profile.id} onClick={() => openReviewModal(profile)}><div className="profile-avatar">{getInitials(profile.full_name)}</div><div className="profile-info"><h4>{profile.full_name}</h4><p>{profile.job_title}</p><span className="profile-company">{profile.company}</span></div><div className="profile-score" style={{ color: getScoreColor(profile.credibility_score) }}>{profile.credibility_score}</div></div>))}</div>
                            </div>
                        </div>
                    </div>
                )}

                {activeMenu === 'applications' && (
                    <div className="vetting-content">
                        <div className="content-header"><h1>All Applications</h1><select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="status-filter"><option value="">All</option><option value="PENDING">Pending</option><option value="UNDER_REVIEW">Under Review</option><option value="APPROVED">Approved</option><option value="DECLINED">Declined</option></select></div>
                        <div className="applications-section full-width"><div className="applications-table"><div className="table-header"><span className="col-applicant">Applicant</span><span className="col-role">Current Role</span><span className="col-applied">Applied</span><span className="col-score">Score</span><span className="col-status">Status</span><span className="col-actions">Actions</span></div>{applications.map(app => (<div className="table-row" key={app.id}><div className="col-applicant"><span className="applicant-avatar">{getInitials(app.full_name)}</span><span className="applicant-name">{app.full_name}</span></div><div className="col-role"><span className="role-title">{app.job_title}</span><span className="role-company">{app.company}</span></div><div className="col-applied">{formatDate(app.submitted_at)}</div><div className="col-score"><span className="score-value" style={{ color: getScoreColor(app.ai_score) }}>{app.ai_score || 'N/A'}</span></div><div className="col-status"><span className={`status-badge ${getStatusClass(app.status)}`}>{app.status}</span></div><div className="col-actions"><button className="review-btn" onClick={() => openReviewModal(app)}>Review</button></div></div>))}</div></div>
                    </div>
                )}

                {activeMenu === 'approved' && (
                    <div className="vetting-content">
                        <div className="content-header"><h1>Approved Profiles</h1><p>{approvedProfiles.length} professors approved</p></div>
                        <div className="approved-profiles-grid large">{approvedProfiles.map(profile => (<div className="approved-profile-card large" key={profile.id} onClick={() => openReviewModal(profile)}><div className="profile-avatar large">{getInitials(profile.full_name)}</div><div className="profile-info"><h4>{profile.full_name}</h4><p className="profile-title">{profile.job_title}</p><p className="profile-company">{profile.company}</p><div className="profile-skills">{(profile.skills || []).slice(0, 3).map((s, i) => <span key={i} className="mini-tag">{s}</span>)}</div></div><div className="profile-meta"><div className="profile-score large" style={{ color: getScoreColor(profile.credibility_score) }}>{profile.credibility_score}</div><span className="approved-date">Approved {formatDate(profile.approved_at)}</span></div></div>))}</div>
                    </div>
                )}

                {activeMenu === 'interviews' && (
                    <div className="vetting-content">
                        <div className="content-header">
                            <h1>Interview Scheduling</h1>
                            <p>{interviewQueue.length} professors awaiting interview</p>
                        </div>
                        <div className="applications-section full-width">
                            <div className="applications-table interviews-table">
                                <div className="table-header">
                                    <span className="col-applicant">Professor</span>
                                    <span className="col-role">Current Role</span>
                                    <span className="col-applied">Approved On</span>
                                    <span className="col-preferred">Preferred Times</span>
                                    <span className="col-status">Interview Status</span>
                                    <span className="col-actions">Schedule Interview</span>
                                </div>

                                {interviewQueue.length === 0 ? (
                                    <div className="empty-state"><p>No professors awaiting interview</p></div>
                                ) : (
                                    interviewQueue.map(professor => (
                                        <div className="table-row" key={professor.id}>
                                            <div className="col-applicant">
                                                <span className="applicant-avatar">{getInitials(professor.full_name)}</span>
                                                <span className="applicant-name">{professor.full_name}</span>
                                            </div>
                                            <div className="col-role">
                                                <span className="role-title">{professor.job_title}</span>
                                                <span className="role-company">{professor.company}</span>
                                            </div>
                                            <div className="col-applied">{formatDate(professor.approved_at)}</div>
                                            <div className="col-preferred">
                                                <div className="preferred-slots-mini">
                                                    {[1, 2, 3].map(i => {
                                                        const time = professor[`preferred_interview_time_${i}`];
                                                        const slotId = professor.preferred_slot_ids && professor.preferred_slot_ids[i - 1];

                                                        if (!time) return null;

                                                        return (
                                                            <div
                                                                key={i}
                                                                className="pref-slot-pill clickable"
                                                                title={slotId ? "Pre-selected slot" : "Schedule at this time"}
                                                                onClick={() => {
                                                                    setSchedulingModal({
                                                                        show: true,
                                                                        professor,
                                                                        selectedSlotId: slotId || '',
                                                                        customTimeslot: time,
                                                                        meetingLink: professor.interview_meeting_link || 'https://meet.google.com/...'
                                                                    });
                                                                    fetchAvailableAdminSlots();
                                                                }}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    border: slotId ? '1px solid #10b981' : '1px solid #3b82f6',
                                                                    color: slotId ? '#047857' : '#1d4ed8',
                                                                    background: slotId ? '#ecfdf5' : '#eff6ff',
                                                                    marginBottom: '4px'
                                                                }}
                                                            >
                                                                {formatDateTime(time)} {slotId ? '✓' : ''}
                                                            </div>
                                                        );
                                                    })}
                                                    {!professor.preferred_interview_time_1 && <span className="text-muted">No slots suggested</span>}
                                                </div>
                                            </div>
                                            <div className="col-status">
                                                <span className={`status-badge ${professor.interview_status === 'SCHEDULED' ? 'approved' :
                                                    professor.interview_status === 'COMPLETED' ? 'approved' :
                                                        professor.interview_status === 'PROPOSED' ? 'review' : 'pending'
                                                    }`}>
                                                    {professor.interview_status === 'SCHEDULED' ? `Scheduled: ${formatDate(professor.interview_timeslot)}` :
                                                        professor.interview_status === 'PROPOSED' ? `Proposed: ${formatDate(professor.interview_timeslot)}` :
                                                            professor.interview_status === 'COMPLETED' ? 'Done' : 'Pending'}
                                                </span>
                                            </div>
                                            <div className="col-actions">
                                                {professor.interview_status === 'COMPLETED' ? (
                                                    <button className="action-btn approve" style={{ padding: '6px 12px' }} onClick={() => openReviewModal(professor)}>
                                                        Final Review
                                                    </button>
                                                ) : (
                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                        <button
                                                            className="action-btn"
                                                            style={{
                                                                background: 'var(--primary-dim)',
                                                                color: 'var(--primary)',
                                                                border: '1px solid var(--primary-dim)',
                                                                padding: '6px 12px',
                                                                fontSize: '12px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                borderRadius: '6px',
                                                                fontWeight: '600'
                                                            }}
                                                            onClick={() => {
                                                                setSchedulingModal({
                                                                    show: true,
                                                                    professor,
                                                                    selectedSlotId: '',
                                                                    customTimeslot: professor.preferred_interview_time_1 || '',
                                                                    meetingLink: professor.interview_meeting_link || 'https://meet.google.com/...'
                                                                });
                                                                fetchAvailableAdminSlots();
                                                            }}
                                                        >
                                                            <Video size={14} style={{ marginRight: '6px' }} />
                                                            {professor.interview_status === 'SCHEDULED' ? 'Reschedule' : 'Schedule'}
                                                        </button>

                                                        {professor.interview_status === 'SCHEDULED' && (
                                                            <button className="action-btn" title="Mark as Completed" onClick={() => handleMarkInterviewComplete(professor.id)}>
                                                                ✓
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {activeMenu === 'training' && (
                    <div className="vetting-content">
                        <div className="content-header">
                            <h1>Training Stage Management</h1>
                            <p>{trainingQueue.length} professors in training phase</p>
                        </div>
                        <div className="applications-section full-width">
                            <div className="applications-table interviews-table">
                                <div className="table-header">
                                    <span className="col-applicant">Professor</span>
                                    <span className="col-role">Current Role</span>
                                    <span className="col-status">Training Status</span>
                                    <span className="col-preferred">Schedule Details</span>
                                    <span className="col-actions">Actions</span>
                                </div>

                                {trainingQueue.length === 0 ? (
                                    <div className="empty-state"><p>No professors in training phase</p></div>
                                ) : (
                                    trainingQueue.map(item => (
                                        <div className="table-row" key={item.id}>
                                            <div className="col-applicant">
                                                <span className="applicant-avatar">{getInitials(item.full_name)}</span>
                                                <span className="applicant-name">{item.full_name}</span>
                                            </div>
                                            <div className="col-role">
                                                <span className="role-title">{item.job_title}</span>
                                                <span className="role-company">{item.company}</span>
                                            </div>
                                            <div className="col-status">
                                                <span className={`status-badge ${item.training_status === 'SCHEDULED' ? 'active' : 'pending'}`}>
                                                    {item.training_status}
                                                </span>
                                            </div>
                                            <div className="col-preferred">
                                                {item.training_status === 'SCHEDULED' ? (
                                                    <div style={{ fontSize: '13px' }}>
                                                        <div>📅 {new Date(item.training_details.date).toLocaleDateString()}</div>
                                                        <div className="text-muted">🔗 <a href={item.training_details.meeting_link} target="_blank" rel="noreferrer">Join Link</a></div>
                                                    </div>
                                                ) : <span className="text-muted">Not yet scheduled</span>}
                                            </div>
                                            <div className="col-actions">
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <button className="action-btn" title="Schedule Training" onClick={() => handleScheduleTraining(item.id)}>
                                                        📅
                                                    </button>
                                                    {item.training_status === 'SCHEDULED' && (
                                                        <button className="action-btn approve" title="Mark as Completed" onClick={() => handleMarkTrainingComplete(item.id)}>
                                                            ✓
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {activeMenu === 'availability' && (
                    <div className="vetting-content">
                        <div className="content-header">
                            <div>
                                <h1>Manage My Interview Availability</h1>
                                <p>Drag on the calendar to select slots or click an existing slot to delete.</p>
                            </div>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    className="smart-schedule-btn"
                                    onClick={() => setSmartModal({ ...smartModal, show: true })}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        background: '#4318ff',
                                        color: 'white',
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Zap size={18} /> Smart Schedule
                                </button>
                            </div>
                        </div>

                        <div className="calendar-legend" style={{
                            display: 'flex',
                            gap: '20px',
                            marginBottom: '20px',
                            background: 'var(--engine-black)',
                            padding: '12px 20px',
                            borderRadius: '12px',
                            border: '1px solid var(--border)',
                            fontSize: '14px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#10b981' }}></span>
                                <span style={{ color: 'var(--text-muted)' }}>Green: Available</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#ef4444' }}></span>
                                <span style={{ color: 'var(--text-muted)' }}>Red: Booked</span>
                            </div>
                        </div>

                        <div className="availability-calendar-container" style={{
                            background: 'var(--graphite-dark)',
                            padding: '24px',
                            borderRadius: '16px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                            height: '700px'
                        }}>
                            <FullCalendar
                                plugins={[timeGridPlugin, interactionPlugin]}
                                initialView="timeGridWeek"
                                headerToolbar={{
                                    left: 'prev,next today',
                                    center: 'title',
                                    right: 'timeGridWeek,timeGridDay'
                                }}
                                selectable={true}
                                selectMirror={true}
                                dayMaxEvents={true}
                                weekends={true}
                                events={calendarEvents}
                                select={handleCalendarSelect}
                                eventClick={handleEventClick}
                                slotMinTime="08:00:00"
                                slotMaxTime="22:00:00"
                                allDaySlot={false}
                                height="100%"
                                slotDuration="00:30:00"
                                slotLabelInterval="01:00"
                                nowIndicator={true}
                            />
                        </div>
                    </div>
                )}
            </main>

            {/* Inline Review Modal */}
            {
                showReviewModal && selectedApp && (
                    <div className="modal-overlay full-screen-review" onClick={() => setShowReviewModal(false)}>
                        <div className="review-modal" onClick={e => e.stopPropagation()}>
                            {modalLoading && (
                                <div className="modal-loading-overlay">
                                    <div className="spinner"></div>
                                    <p>Loading application data...</p>
                                </div>
                            )}
                            <div className="review-modal-header">
                                <button className="modal-close" onClick={() => setShowReviewModal(false)}>×</button>
                                <div className="modal-title-row">
                                    <div>
                                        <h2>Application Review</h2>
                                        <p className="modal-subtitle">Review and make decision on professor application</p>
                                    </div>
                                    {selectedApp.analysisPending && (
                                        <button className="refresh-btn" onClick={refreshApplicationData} disabled={modalLoading}>
                                            🔄 Refresh Data
                                        </button>
                                    )}
                                </div>

                                {selectedApp.analysisPending && (
                                    <div className="analysis-pending-banner">
                                        ⏳ AI Analysis is still processing. Click "Refresh Data" to check for updates.
                                    </div>
                                )}

                                <div className="applicant-header">
                                    <div className="applicant-avatar">{getInitials(selectedApp.full_name)}</div>
                                    <div className="applicant-info">
                                        <h3>{selectedApp.full_name}</h3>
                                        <p>🏢 {selectedApp.job_title} at {selectedApp.company}</p>
                                        <p className="applied-date">Applied: {formatDate(selectedApp.submitted_at || selectedApp.approved_at)}</p>
                                    </div>
                                    <div className="credibility-badge">
                                        <span className="score-number" style={{ color: getScoreColor(modalScore) }}>{modalScore}</span>
                                        <span className="score-label">Credibility</span>
                                    </div>
                                </div>
                            </div>

                            <div className="review-tabs">
                                {['overview', 'experience', 'documents', 'conflicts', 'timeline'].map(tab => (
                                    <button key={tab} className={`review-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </button>
                                ))}
                            </div>

                            <div className="review-content">
                                {activeTab === 'overview' && (
                                    <div className="overview-tab">
                                        <div className="info-card">
                                            <h4>Professional Summary</h4>
                                            <p>{selectedApp.professional_summary || getMockDetailData(selectedApp).professional_summary}</p>
                                        </div>

                                        <div className="info-card" style={{ marginTop: '24px' }}>
                                            <h4>Skills & Expertise</h4>
                                            <div className="skill-tags" style={{ marginTop: '12px' }}>
                                                {(selectedApp.skills || ['Data Science', 'AI', 'Leadership']).map((skill, i) => (
                                                    <span key={i} className="skill-tag">{skill}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="overview-grid">
                                            <div className="info-card credibility-card">
                                                <h4>Credibility Score</h4>
                                                <div className="big-score" style={{ color: getScoreColor(modalScore) }}>{modalScore}</div>
                                                <p className="score-subtitle">out of 100</p>
                                                <div className="score-breakdown">
                                                    {Object.entries(modalBreakdown).map(([key, val]) => (
                                                        <div key={key} className="breakdown-item">
                                                            <span className="breakdown-icon">{key === 'experience' ? '👤' : key === 'teaching_rating' ? '🎓' : key === 'research' ? '' : key === 'interview_vetting' ? '💬' : '✓'}</span>
                                                            <span className="breakdown-label">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                                                            <span className="breakdown-score">{val.score}/{val.max}</span>
                                                            <div className="breakdown-bar"><div className="breakdown-fill" style={{ width: `${(val.score / val.max) * 100}%` }}></div></div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="info-card quick-stats-card">
                                                <h4>Quick Stats</h4>
                                                <div className="quick-stat"><span>Years of Exp</span><strong>{selectedApp.years_of_experience || 0} Years</strong></div>
                                                <div className="quick-stat"><span>Highest Education</span><strong>{selectedApp.highest_education || 'N/A'}</strong></div>
                                                <div className="quick-stat">
                                                    <span>Engagement</span>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end', marginTop: '4px' }}>
                                                        {selectedApp.engagement_categories?.map(cat => (
                                                            <span key={cat} style={{ fontSize: '10px', background: 'var(--blue-dim)', color: 'var(--cyan)', padding: '2px 6px', borderRadius: '4px', border: '1px solid var(--border)' }}>{cat}</span>
                                                        ))}
                                                        {(!selectedApp.engagement_categories || selectedApp.engagement_categories.length === 0) && <strong style={{ fontSize: '0.9rem' }}>N/A</strong>}
                                                    </div>
                                                </div>
                                                <div className="quick-stat"><span>Publications</span><strong>{selectedApp.publications || 0}</strong></div>
                                                <div className="quick-stat"><span>Prior Teaching</span><strong>{selectedApp.prior_teaching || 'Not specified'}</strong></div>
                                            </div>
                                        </div>

                                        {(selectedApp.preferred_interview_time_1 || selectedApp.preferred_interview_time_2 || selectedApp.preferred_interview_time_3) && (
                                            <div className="info-card">
                                                <h4>Preferred Interview Times</h4>
                                                <p className="modal-subtitle">Candidate suggests these timeslots for the vetting call:</p>
                                                <div className="preferred-times-grid">
                                                    {selectedApp.preferred_interview_time_1 && (
                                                        <div className="preferred-time-item">
                                                            <span className="time-label">Option 1</span>
                                                            <span className="time-value">{formatDateTime(selectedApp.preferred_interview_time_1)}</span>
                                                        </div>
                                                    )}
                                                    {selectedApp.preferred_interview_time_2 && (
                                                        <div className="preferred-time-item">
                                                            <span className="time-label">Option 2</span>
                                                            <span className="time-value">{formatDateTime(selectedApp.preferred_interview_time_2)}</span>
                                                        </div>
                                                    )}
                                                    {selectedApp.preferred_interview_time_3 && (
                                                        <div className="preferred-time-item">
                                                            <span className="time-label">Option 3</span>
                                                            <span className="time-value">{formatDateTime(selectedApp.preferred_interview_time_3)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="info-card"><h4>Review Notes</h4><textarea className="review-notes-input" placeholder="Add your review notes and recommendations..." value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={4} style={{ width: '100%', outline: 'none', border: '1px solid var(--border)', marginTop: '10px' }} /></div>
                                    </div>
                                )}

                                {activeTab === 'experience' && (
                                    <div className="experience-tab">
                                        <div className="info-card"><h4>Work Experience</h4><div className="experience-list">{(selectedApp.work_experience || getMockDetailData(selectedApp).work_experience).map((exp, i) => (<div key={i} className="experience-item"><span className="exp-icon">🏢</span><div className="exp-details"><strong>{exp.title}</strong><span className="exp-company">{exp.company}</span><span className="exp-period">{exp.period}</span></div></div>))}</div></div>
                                        <div className="info-card"><h4>Education</h4><div className="experience-list">{(selectedApp.education || getMockDetailData(selectedApp).education).map((edu, i) => (<div key={i} className="experience-item"><span className="exp-icon">🎓</span><div className="exp-details"><strong>{edu.degree}</strong><span className="exp-company">{edu.institution}</span><span className="exp-period">{edu.year}</span></div></div>))}</div></div>
                                    </div>
                                )}

                                {activeTab === 'documents' && (
                                    <div className="documents-tab"><div className="info-card"><h4>Submitted Documents</h4><div className="documents-list">{(selectedApp.documents || []).map((doc, i) => (<div key={i} className="document-item"><span className="doc-icon"></span><span className="doc-name">{doc.name}</span><span className={`doc-status ${doc.status}`}>{doc.status === 'verified' ? '✓ Verified' : doc.status === 'pending' ? '⏳ Pending' : '✕ Missing'}</span><button className="doc-view-btn" onClick={() => handleViewDocument(doc)} disabled={doc.status === 'missing'}>{doc.status === 'missing' ? 'N/A' : 'View'}</button></div>))}</div></div></div>
                                )}

                                {activeTab === 'conflicts' && (
                                    <div className="conflicts-tab"><div className="info-card"><h4>Conflict of Interest Check</h4>{(selectedApp.conflicts || []).length === 0 ? (<div className="no-conflicts"><span className="check-icon">✓</span><p>No conflicts of interest detected</p></div>) : (<div className="conflicts-list">{selectedApp.conflicts.map((c, i) => (<div key={i} className="conflict-item">{c}</div>))}</div>)}</div></div>
                                )}

                                {activeTab === 'timeline' && (
                                    <div className="timeline-tab"><div className="info-card"><div className="timeline-list">{(selectedApp.timeline || getMockDetailData(selectedApp).timeline).map((item, i) => (<div key={i} className={`timeline-item ${item.status}`}><div className="timeline-marker">{item.status === 'completed' ? <span className="marker-done">✓</span> : item.status === 'current' ? <span className="marker-current">◯</span> : <span className="marker-pending">○</span>}</div><div className="timeline-content"><span className={`timeline-stage ${item.status}`}>{item.stage}</span>{item.date && <span className="timeline-date">{formatDate(item.date)}</span>}</div></div>))}</div></div></div>
                                )}
                            </div>

                            {selectedApp.status !== 'APPROVED' && (
                                <div className="review-actions">
                                    <button className="action-btn approve" onClick={() => handleDecision('APPROVED')} disabled={processing}>✓ Approve</button>
                                    <button className="action-btn conditional" onClick={() => handleDecision('CONDITIONAL')} disabled={processing}>ⓘ Conditional Approval</button>
                                    <button className="action-btn decline" onClick={() => handleDecision('DECLINED')} disabled={processing}>✕ Decline</button>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }

            {/* Training Schedule Modal */}
            <style>
                {`
                .spinner-small {
                    width: 18px;
                    height: 18px;
                    border: 2px solid #ffffff;
                    border-radius: 50%;
                    border-top-color: transparent;
                    animation: spin-small 0.6s linear infinite;
                }
                @keyframes spin-small {
                    to { transform: rotate(360deg); }
                }
                `}
            </style>
            {trainingModal.show && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10002,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        background: 'var(--graphite-dark)',
                        borderRadius: '16px',
                        padding: '32px',
                        maxWidth: '550px',
                        width: '90%',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        animation: 'modalSlideIn 0.3s ease-out'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                background: 'var(--primary-soft, rgba(25, 211, 255, 0.1))',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                fontSize: '32px'
                            }}>
                                📅
                            </div>
                            <h2 style={{
                                margin: '0 0 8px',
                                fontSize: '24px',
                                fontWeight: '700',
                                color: 'var(--proto-white)'
                            }}>
                                Schedule Training
                            </h2>
                            <p style={{
                                margin: 0,
                                color: 'var(--text-muted)',
                                fontSize: '15px',
                                lineHeight: '1.6'
                            }}>
                                Enter the training session details
                            </p>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: 'var(--text-muted)'
                            }}>
                                Training Date
                            </label>
                            <input
                                type="date"
                                value={trainingModal.formData.training_date}
                                onChange={(e) => setTrainingModal({
                                    ...trainingModal,
                                    formData: { ...trainingModal.formData, training_date: e.target.value }
                                })}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    border: '2px solid var(--border)',
                                    fontSize: '15px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#4318ff'}
                                onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: 'var(--text-muted)'
                            }}>
                                Start Time
                            </label>
                            <input
                                type="text"
                                value={trainingModal.formData.start_time}
                                onChange={(e) => setTrainingModal({
                                    ...trainingModal,
                                    formData: { ...trainingModal.formData, start_time: e.target.value }
                                })}
                                placeholder="e.g. 11:00 AM"
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    border: '2px solid var(--border)',
                                    background: 'var(--bg-page)',
                                    color: 'var(--proto-white)',
                                    fontSize: '15px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: 'var(--text-muted)'
                            }}>
                                Meeting Link
                            </label>
                            <input
                                type="text"
                                value={trainingModal.formData.meeting_link}
                                onChange={(e) => setTrainingModal({
                                    ...trainingModal,
                                    formData: { ...trainingModal.formData, meeting_link: e.target.value }
                                })}
                                placeholder="https://meet.google.com/..."
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    border: '2px solid var(--border)',
                                    background: 'var(--bg-page)',
                                    color: 'var(--proto-white)',
                                    fontSize: '15px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'var(--primary)' }
                                onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: 'var(--text-muted)'
                            }}>
                                Trainer Name
                            </label>
                            <input
                                type="text"
                                value={trainingModal.formData.trainer_name}
                                onChange={(e) => setTrainingModal({
                                    ...trainingModal,
                                    formData: { ...trainingModal.formData, trainer_name: e.target.value }
                                })}
                                placeholder="Corporate Training Team"
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    border: '2px solid var(--border)',
                                    fontSize: '15px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#4318ff'}
                                onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                            />
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={() => setTrainingModal({ show: false, appId: null, formData: {} })}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    border: '2px solid var(--border)',
                                    background: 'var(--graphite-dark)',
                                    color: 'var(--text-muted)',
                                    fontWeight: '600',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = 'var(--border)';
                                    e.target.style.background = 'var(--bg-soft)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = 'var(--border)';
                                    e.target.style.background = 'transparent';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitTrainingSchedule}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: 'var(--primary)',
                                    color: '#0B0F14',
                                    fontWeight: '800',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: 'var(--shadow-cyan)'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'var(--primary-dark)'}
                                onMouseLeave={(e) => e.target.style.background = 'var(--primary)'}
                            >
                                Schedule Training
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Prompt Modal */}
            {promptModal.show && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10003,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        background: 'var(--graphite-dark)',
                        borderRadius: '16px',
                        padding: '32px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        animation: 'modalSlideIn 0.3s ease-out'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                background: 'var(--primary-soft)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                fontSize: '32px'
                            }}>
                                ✏️
                            </div>
                            <h2 style={{
                                margin: '0 0 8px',
                                fontSize: '24px',
                                fontWeight: '700',
                                color: 'var(--proto-white)'
                            }}>
                                {promptModal.title}
                            </h2>
                            <p style={{
                                margin: 0,
                                color: 'var(--text-muted)',
                                fontSize: '15px',
                                lineHeight: '1.6'
                            }}>
                                {promptModal.message}
                            </p>
                        </div>
                        <input
                            type="text"
                            defaultValue={promptModal.defaultValue}
                            id="prompt-modal-input"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const value = e.target.value;
                                    promptModal.onSubmit(value);
                                }
                                if (e.key === 'Escape') {
                                    setPromptModal({ show: false, title: '', message: '', defaultValue: '', onSubmit: null });
                                }
                            }}
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: '2px solid var(--border)',
                                fontSize: '15px',
                                marginBottom: '24px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4318ff'}
                            onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                        />
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={() => setPromptModal({ show: false, title: '', message: '', defaultValue: '', onSubmit: null })}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    border: '2px solid var(--border)',
                                    background: 'var(--graphite-dark)',
                                    color: 'var(--text-muted)',
                                    fontWeight: '600',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = 'var(--border)';
                                    e.target.style.background = '#f8fafc';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = 'var(--border)';
                                    e.target.style.background = 'white';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    const value = document.getElementById('prompt-modal-input').value;
                                    promptModal.onSubmit(value);
                                }}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: '#4318ff',
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#3311cc'}
                                onMouseLeave={(e) => e.target.style.background = '#4318ff'}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Scheduling Modal */}
            {schedulingModal.show && (
                <div className="modal-overlay" style={{ zIndex: 10005 }}>
                    <div className="review-modal" style={{ maxWidth: '480px' }}>
                        <div className="review-modal-header" style={{ textAlign: 'center' }}>
                            <button className="modal-close" onClick={() => setSchedulingModal({ ...schedulingModal, show: false })}>×</button>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📅</div>
                            <h2 style={{ fontSize: '24px', fontWeight: '700' }}>Schedule Interview</h2>
                            <p className="modal-subtitle">for {schedulingModal.professor?.full_name}</p>
                        </div>

                        <div style={{ padding: '24px', overflowY: 'auto' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                    Option A: Pick from Your Available Slots
                                </label>
                                <select
                                    value={schedulingModal.selectedSlotId}
                                    onChange={(e) => {
                                        const slotId = e.target.value;
                                        const slot = availableAdminSlots.find(s => s.id === parseInt(slotId));
                                        setSchedulingModal(prev => ({
                                            ...prev,
                                            selectedSlotId: slotId,
                                            customTimeslot: slot ? slot.start_time : prev.customTimeslot
                                        }));
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        border: '2px solid var(--border)',
                                        fontSize: '14px',
                                        outline: 'none',
                                        background: 'var(--graphite-dark)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="">-- No predefined slot selected --</option>
                                    {availableAdminSlots.map(slot => (
                                        <option key={slot.id} value={slot.id}>
                                            {formatDateTime(slot.start_time)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                    Option B: Set Custom Date & Time
                                </label>
                                <input
                                    type="datetime-local"
                                    value={schedulingModal.customTimeslot ?
                                        new Date(new Date(schedulingModal.customTimeslot).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 16) : ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSchedulingModal(prev => ({
                                            ...prev,
                                            customTimeslot: val,
                                            selectedSlotId: ''
                                        }));
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        border: '2px solid var(--border)',
                                        fontSize: '14px',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '28px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                    Meeting Link <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <Video size={16} style={{ position: 'absolute', left: '14px', top: '14px', color: 'var(--text-faint)' }} />
                                    <input
                                        type="text"
                                        value={schedulingModal.meetingLink}
                                        onChange={(e) => setSchedulingModal(prev => ({ ...prev, meetingLink: e.target.value }))}
                                        placeholder="https://zoom.us/j/..."
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px 12px 40px',
                                            borderRadius: '8px',
                                            border: '2px solid var(--border)',
                                            fontSize: '14px',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    onClick={() => setSchedulingModal({ ...schedulingModal, show: false })}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '2px solid var(--border)',
                                        background: 'var(--graphite-dark)',
                                        color: 'var(--text-muted)',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSchedulingSubmit}
                                    disabled={processing}
                                    style={{
                                        flex: 1,
                                        padding: '14px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: '#4318ff',
                                        color: '#fff',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 12px rgba(67, 24, 255, 0.2)',
                                        opacity: processing ? 0.7 : 1
                                    }}
                                >
                                    {processing ? 'Saving...' : 'Confirm Interview'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {confirmModal.show && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10001,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        background: 'var(--graphite-dark)',
                        borderRadius: '16px',
                        padding: '32px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        animation: 'modalSlideIn 0.3s ease-out'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                background: '#fef2f2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                fontSize: '32px'
                            }}>
                                ⚠️
                            </div>
                            <h2 style={{
                                margin: '0 0 8px',
                                fontSize: '24px',
                                fontWeight: '700',
                                color: '#1f2937'
                            }}>
                                Confirm Action
                            </h2>
                            <p style={{
                                margin: 0,
                                color: '#6b7280',
                                fontSize: '15px',
                                lineHeight: '1.6'
                            }}>
                                {confirmModal.message}
                            </p>
                        </div>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    border: '2px solid var(--border)',
                                    background: 'var(--graphite-dark)',
                                    color: 'var(--text-muted)',
                                    fontWeight: '600',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = 'var(--border)';
                                    e.target.style.background = '#f8fafc';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = 'var(--border)';
                                    e.target.style.background = 'white';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => confirmModal.onConfirm()}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: '#ef4444',
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                                onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notifications */}
            <div style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 10000,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
            }}>
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        style={{
                            background: toast.type === 'success' ? '#10b981' : '#ef4444',
                            color: '#fff',
                            padding: '16px 24px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            minWidth: '300px',
                            maxWidth: '500px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            animation: 'slideIn 0.3s ease-out',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        <span style={{ fontSize: '20px' }}>
                            {toast.type === 'success' ? '\u2713' : '\u2715'}
                        </span>
                        <span style={{ flex: 1 }}>{toast.message}</span>
                        <button
                            onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '18px',
                                padding: '0',
                                opacity: 0.8,
                                transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '1'}
                            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>

            <style>{`
                @keyframes slideIn {
                    from {
                        transform: translateX(400px);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                @keyframes modalSlideIn {
                    from {
                        transform: translateY(-50px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
            `}</style>
            {/* Smart Schedule Modal */}
            {smartModal.show && (
                <div className="modal-overlay" onClick={() => setSmartModal({ ...smartModal, show: false })}>
                    <div className="modal-content" style={{ maxWidth: '450px', padding: '32px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ background: '#fef3c7', padding: '10px', borderRadius: '50%' }}>
                                    <Zap size={24} style={{ color: '#d97706' }} />
                                </div>
                                <h2 style={{ margin: 0, fontSize: '20px' }}>Smart Schedule</h2>
                            </div>
                            <button className="modal-close" onClick={() => setSmartModal({ ...smartModal, show: false })}>×</button>
                        </div>

                        <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
                            Select the days and time range you are generally available for interviews.
                            We will generate slots for the next 4 weeks.
                        </p>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--proto-white)', marginBottom: '12px' }}>Select Active Days</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                                    const isSelected = smartModal.selectedDays.includes(day);
                                    return (
                                        <button
                                            key={day}
                                            onClick={() => {
                                                const newDays = isSelected
                                                    ? smartModal.selectedDays.filter(d => d !== day)
                                                    : [...smartModal.selectedDays, day];
                                                setSmartModal({ ...smartModal, selectedDays: newDays });
                                            }}
                                            style={{
                                                padding: '8px 14px',
                                                borderRadius: '50px',
                                                fontSize: '13px',
                                                fontWeight: 500,
                                                border: isSelected ? '1px solid #4318ff' : '1px solid var(--border)',
                                                cursor: 'pointer',
                                                backgroundColor: isSelected ? '#4318ff' : 'white',
                                                color: isSelected ? 'white' : 'var(--text-muted)',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {day.substring(0, 3)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '32px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--proto-white)', marginBottom: '8px' }}>Start Time</label>
                                <input
                                    type="time"
                                    value={smartModal.startTime}
                                    onChange={e => setSmartModal({ ...smartModal, startTime: e.target.value })}
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--proto-white)', marginBottom: '8px' }}>End Time</label>
                                <input
                                    type="time"
                                    value={smartModal.endTime}
                                    onChange={e => setSmartModal({ ...smartModal, endTime: e.target.value })}
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                                />
                            </div>
                        </div>

                        <button
                            className="pop-btn-primary"
                            style={{
                                width: '100%',
                                padding: '14px',
                                background: '#4318ff',
                                border: 'none',
                                borderRadius: '12px',
                                color: 'white',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                cursor: 'pointer'
                            }}
                            onClick={handleSmartScheduleSubmit}
                            disabled={processing}
                        >
                            {processing ? <div className="spinner-small"></div> : <Zap size={18} />}
                            {processing ? 'Generating...' : 'Generate Slots'}
                        </button>
                    </div>
                </div>
            )}
        </div >
    );
}

export default VettingPage;
